import AsyncStorage from "@react-native-async-storage/async-storage";
import { ComponentType } from "react";
// that's the unique place where useDispatch should be import
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Provider, useDispatch } from "react-redux";
import { tReduxDispatch } from "src/redux/types";
import { applyMiddleware, combineReducers, createStore, Reducer } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { PersistConfig, persistReducer, persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import Thunk from "redux-thunk";
import { ApplicationReducer } from "./Application/reducer";
/**
 * Import reducers here!
 * Make sure that when we create new reducers we add them here!
 * like 'SampleReducer' below
 *
 * Also make sure to update the 'tReduxState'
 * type with any new reducers we add in the 'types.ts'
 * file in this folder!
 */
// import { SampleReducer } from "./template/reducer";
import { BankLinkReducer } from "./BankAccountLink/reducer";
import { BrandLandingReducer } from "./BrandLanding/reducer";
import { CompanyReducer } from "./company/reducer";
import { ConfigsReducer } from "./configs/reducer";
import { ConnectionManagerReducer } from "./connectionManager/reducer";
import { DraftOrderReducer } from "./draftCart/reducer";
import { ErrorHandlerReducer } from "./errorHandler/reducer";
import { BrandProductReducer } from "./firebaseProducts/reducer";
import { LoadingReducer } from "./loading/reducer";
import { MessageThreadsReducer } from "./messages/reducer";
import { ModalsReducer } from "./modals/reducer";
import { NavigationReducer } from "./navigation/reducer";
import { ProductDetailsReducer } from "./ProductDetails/reducer";
import { ProductReducer } from "./products/reducer";
import { ProfileReducer } from "./profile/reducer";
import { SearchReducer } from "./search/reducer";
import { tReduxState } from "./types";
import { UserReducer } from "./user/reducer";
import { AdminReducer } from "./Admin/reducer";
import { AdminSettingReducer } from "./AdminSettings/reducer";
import { ExclusiveBrandReducer } from "./exclusiveBrands/reducer";
import { FiltersReducer } from "./filters/reducer";
import { AdminUserReducer } from "./AdminUser/reducer";
import { TableConfigReducer } from "./TableConfig/reducer";

/** *** */
const RootReducer = combineReducers({
  UserReducer,
  MessageThreadsReducer,
  NavigationReducer,
  ConnectionManagerReducer,
  CompanyReducer,
  ConfigsReducer,
  ErrorHandlerReducer,
  DraftOrderReducer,
  LoadingReducer,
  ModalsReducer,
  SearchReducer,
  ProductReducer,
  ProfileReducer,
  BrandProductReducer,
  BankLinkReducer,
  ApplicationReducer,
  BrandLandingReducer,
  ProductDetailsReducer,
  AdminReducer,
  AdminSettingReducer,
  ExclusiveBrandReducer,
  FiltersReducer,
  AdminUserReducer,
  TableConfigReducer,
});

/**
 * Configures the redux store
 * @returns <store, persistedStore> the redux store and redux-persist store
 * (to be used with the redux / redux-persist provider components)
 * see https://github.com/rt2zz/redux-persist for more details on the persisted store
 */
const configureStore = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const persistConfig: PersistConfig<any, any, any, any> = {
    key: "root",
    storage: AsyncStorage,
    blacklist: [
      "MessageThreadsReducer",
      "NavigationReducer",
      "ConnectionManagerReducer",
      "CompanyReducer",
      "ConfigsReducer",
      "ErrorHandlerReducer",
      "DraftOrderReducer",
      "LoadingReducer",
      "ModalsReducer",
      "SearchReducer",
      "ProductReducer",
      "BrandProductReducer",
      "BankLinkReducer",
      "ApplicationReducer",
      "BrandLandingReducer",
      "ProductDetailsReducer",
      "AdminReducer",
      "AdminSettingReducer",
      "ExclusiveBrandReducer",
      "FiltersReducer",
      "AdminUserReducer",
    ],
    transforms: [],
  };
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const reducer = persistReducer(persistConfig, RootReducer as unknown as Reducer<tReduxState>);
  let enhancer = applyMiddleware(Thunk);
  if (__DEV__) {
    const devToolConfig = {
      name: "themaintab",
    };
    enhancer = composeWithDevTools(devToolConfig)(enhancer);
  }
  const store = createStore(reducer, enhancer);
  const persistedStore = persistStore(store);
  return {
    store,
    persistedStore,
  };
};

const { store, persistedStore } = configureStore();

/**
 * purgePersistedState
 * purges state from disk and returns a Promise
 * (calls the redux-persist persistedStore.purge method)
 */
const purgePersistedState = (): Promise<unknown> => {
  return persistedStore.purge();
};

/**
 * Redux HOC
 *
 * Provides all child components with the redux store context
 * (i.e. connect and useSelector / useDispatch calls)
 */
const withRedux = (WrappedComponent: ComponentType) => {
  return function ReduxContainer(): JSX.Element {
    return (
      <Provider store={store}>
        <PersistGate persistor={persistedStore}>
          <WrappedComponent />
        </PersistGate>
      </Provider>
    );
  };
};

export const useAppDispatch = (): tReduxDispatch => useDispatch();

export { store, persistedStore, purgePersistedState, withRedux };
